/**
 * Socket.IO Service for Real-time Updates
 * Replaces polling with efficient WebSocket communication
 */

import { io, Socket } from 'socket.io-client';

// Event types matching backend
export type SocketEventType =
  | 'purchase_orders:updated'
  | 'purchase_orders:created'
  | 'purchase_orders:deleted'
  | 'stock:updated'
  | 'stock:alert'
  | 'deliveries:updated'
  | 'deliveries:created'
  | 'notifications:new'
  | 'orders:new'
  | 'orders:updated'
  | 'daily_report:updated'
  | 'temperature:alert'
  | 'lots:updated'
  | 'transfers:updated'
  | 'connected'
  | 'connect_error'
  | 'disconnect';

type EventCallback<T = any> = (data: T) => void;

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    if (this.socket?.connected || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    // Extract base URL without /api path for Socket.IO
    // VITE_API_URL is like "https://api.volailledor.logicieels.cloud/api"
    // Socket.IO needs just "https://api.volailledor.logicieels.cloud"
    let apiUrl = import.meta.env.VITE_API_URL || 'https://api.volailledor.logicieels.cloud/api';

    // Remove trailing /api or /api/ if present
    const baseUrl = apiUrl.replace(/\/api\/?$/, '');

    this.socket = io(baseUrl, {
      path: '/socket.io', // Explicit Socket.IO path
      withCredentials: true, // Send HttpOnly cookie for auth
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupEventHandlers();
  }

  /**
   * Setup core event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[Socket] Connected:', this.socket?.id);
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.notifyListeners('connected', { connected: true, socketId: this.socket?.id });
    });

    this.socket.on('connect_error', (error) => {
      // Suppress expected auth errors (e.g. during logout or before login)
      const msg = error.message || '';
      if (!msg.includes('Authentication required') && !msg.includes('jwt') && !msg.includes('token')) {
        console.error('[Socket] Connection error:', msg);
      }
      this.isConnecting = false;
      this.notifyListeners('connect_error', { error: msg });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      this.notifyListeners('disconnect', { reason });
    });

    // Forward all other events to registered listeners
    this.socket.onAny((event: string, data: any) => {
      this.notifyListeners(event, data);
    });
  }

  /**
   * Disconnect from the server
   *
   * Les abonnements (`listeners`) appartiennent aux composants React, pas a
   * la connexion : un `useSocketEvent` ne s'enregistre qu'une fois, a son
   * montage, et ne se reabonne jamais tout seul. Les vider ici les
   * perdait a chaque reconnexion (meme transitoire) — plus aucun ecran ne
   * recevait la moindre mise a jour temps reel jusqu'au rechargement de la
   * page, en silence.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  /**
   * Subscribe to an event
   * @returns Unsubscribe function
   */
  on<T = any>(event: SocketEventType | string, callback: EventCallback<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from an event
   */
  off<T = any>(event: string, callback: EventCallback<T>): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback as EventCallback);
    }
  }

  /**
   * Subscribe to a room
   */
  subscribe(room: string): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', room);
    }
  }

  /**
   * Unsubscribe from a room
   */
  unsubscribe(room: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', room);
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get socket ID
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Notify all listeners for an event
   */
  private notifyListeners(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[Socket] Error in listener for ${event}:`, error);
        }
      });
    }
  }
}

// Singleton instance
export const socketService = new SocketService();

export default socketService;
